import { createSelector, ParametricSelector, Selector } from "reselect";

import { BettingState, COMBINABLE_LEG_TYPES_LIST } from "@ppb/betslip-core";
import { ApplicationState, BetslipCastContext } from "@ppb/tbd-store";
import { createShallowEqualSelector } from "@ppb/tbd-store/helpers/selectors";
import {
  generateCastGroupIds,
  generateCastRunnersIds,
  groupCombinationsByMarketId,
  isBetBuilder,
  isMultiple,
  isSingleLike,
} from "@ppb/tbd-store/helpers/sportsbook-betting";
import {
  getBetslipGroup,
  getSportsbookConfirmation,
  getSportsbookConfirmationCastContext,
  getSportsbookConfirmationCombinations,
  getSportsbookConfirmationIgnoredBets,
  getSportsbookConfirmationLegs,
  getSportsbookConfirmationRunners,
} from "../store/state/betslip-module/betslip-module-selectors";
import { BetslipApplicationState } from "../store/state/BetslipApplicationState";
import { RunnersMetadata } from "@ppb/tbd-store/state/betting/sportsbook-betting/SportsbookBetting.types";
import { getBettingResolvers } from "../store/state/sportsbook-betting/betting-resolver";
import { CastBetCastTypeItem } from "@ppb/the-wall-common/types";

import { buildCastBet, CastBet, generateCastTypes } from "./betslip-mapper";
import { groupCombinationsByMultiLineTypes, MultipleCombinations } from "../helpers/combinations";
import type { BetslipLocalization } from "../contract";

export const createIsConfirmStep = (): Selector<BetslipApplicationState, boolean> => (state: BetslipApplicationState) =>
  state.betslipModule?.step === "CONFIRM_POTENTIAL";

export const createGetConfirmationMultipleCombinations = (): Selector<BetslipApplicationState, MultipleCombinations> =>
  createSelector(
    [getSportsbookConfirmationCombinations],
    (combinations): MultipleCombinations => groupCombinationsByMultiLineTypes(combinations),
  );

export const createGetConfirmationOneLineMultiple = (): Selector<
  BetslipApplicationState,
  BettingState.Combination | undefined
> =>
  createSelector([getSportsbookConfirmationCombinations], (combinations = {}): BettingState.Combination | undefined => {
    const [firstValidAccumulator] = Object.values(combinations)
      .filter((combination): boolean => isMultiple(combination))
      .sort((combinationA, combinationB): number => combinationA.numLines - combinationB.numLines);

    if (!firstValidAccumulator?.id || !combinations?.[firstValidAccumulator?.id]) {
      return undefined;
    }

    return combinations[firstValidAccumulator.id];
  });

export const createGetConfirmationSelectionIdsSelector = (): Selector<BetslipApplicationState, string[]> =>
  createShallowEqualSelector(
    [getSportsbookConfirmationLegs, getSportsbookConfirmationIgnoredBets],
    (legs, ignoredBets = []): string[] =>
      Object.values(legs)
        .filter(
          ({ legType, runners }): boolean =>
            COMBINABLE_LEG_TYPES_LIST.includes(legType) &&
            !ignoredBets.some((runner): boolean => runner.id === runners[0]),
        )
        .map(({ id }): string => id),
  );

export const createGetBetBuilderConfirmationCombinationIdsSelector = (): Selector<BetslipApplicationState, string[]> =>
  createShallowEqualSelector(
    createSelector([getSportsbookConfirmationCombinations], (combinations) =>
      Object.values(combinations)
        .filter((combination) => isBetBuilder(combination))
        .map(({ id }) => id),
    ),
    (combinationIds: string[]) => combinationIds,
  );

export const getSingleCombinationIdsConfirm = createShallowEqualSelector(
  createSelector(
    [getSportsbookConfirmationLegs, getSportsbookConfirmationCombinations],
    (legs, combinations): string[] =>
      Object.values(combinations)
        .filter((combination): boolean => isSingleLike(combination, legs))
        .sort((combinationA, combinationB): number => combinationA.creationTimestamp - combinationB.creationTimestamp)
        .map(({ id }): string => id),
  ),
  (combinationIds: string[]): string[] => combinationIds,
);

export const createGetConfirmationCastGroupIdsSelector = (): Selector<BetslipApplicationState, string[]> =>
  createShallowEqualSelector(
    createSelector(
      [
        getSportsbookConfirmation,
        (state): RunnersMetadata => getBettingResolvers(getBetslipGroup(state)).getMetadata(state),
      ],
      (confirmationState, bettingMetadata): string[] =>
        generateCastGroupIds(groupCombinationsByMarketId(confirmationState), bettingMetadata),
    ),
    (combinationIds: string[]): string[] => combinationIds,
  );

export const createGetConfirmationCastRunnerIdsSelector = (): ParametricSelector<
  BetslipApplicationState,
  string | undefined,
  string[]
> =>
  createShallowEqualSelector(
    createSelector(
      [
        getSportsbookConfirmationCombinations,
        getSportsbookConfirmationLegs,
        getSportsbookConfirmationRunners,
        (_: ApplicationState, combinationId?: string): string | undefined => combinationId,
      ],
      generateCastRunnersIds,
    ),
    (runnerIds: string[]): string[] => runnerIds,
  );

export const createGetConfirmationCastContextSelector = (): Selector<
  BetslipApplicationState,
  BetslipCastContext | undefined
> =>
  createSelector([getSportsbookConfirmationCastContext], (castContext): BetslipCastContext | undefined => castContext);

export const createGetConfirmationCastBetSelector = () => {
  const getCastContext = createGetConfirmationCastContextSelector();

  return createSelector(
    [
      getSportsbookConfirmation,
      (_: ApplicationState, castGroupId: string): string => castGroupId,
      getCastContext,
      (state: BetslipApplicationState): RunnersMetadata =>
        getBettingResolvers(getBetslipGroup(state)).getMetadata(state),
      (_state: ApplicationState, _castGroupId: string, localization: BetslipLocalization) => localization,
    ],
    (confirmationState, castGroupId, castContext, metadata, localization): CastBet | undefined => {
      if (!confirmationState) {
        return undefined;
      }

      const castGroup = groupCombinationsByMarketId(confirmationState)[castGroupId];

      if (!castGroup) {
        return undefined;
      }

      const context = castContext || { [castGroup.id]: castGroup.combinations[0].id };

      return buildCastBet(castGroup, context, confirmationState, metadata, localization, false);
    },
  );
};

export const createGetConfirmationCastTypesSelector = (): ParametricSelector<
  BetslipApplicationState,
  string,
  CastBetCastTypeItem[]
> =>
  createSelector(
    [getSportsbookConfirmation, (_: ApplicationState, castGroupId: string): string => castGroupId],
    (confirmationState, castGroupId): CastBetCastTypeItem[] => {
      if (!confirmationState) {
        return [];
      }

      const castGroup = groupCombinationsByMarketId(confirmationState)[castGroupId];

      if (!castGroup) {
        return [];
      }

      return generateCastTypes(confirmationState.legs, castGroup);
    },
  );
